/*
  <!-- Approved to ship by Jordan 2026-08-25 (iterate later). NOT LAWYER REVIEWED -->

  Created 2026-08-25 in response to the legal + content review
  (docs/business/legal-content-review-2026-08-25.md, finding P0-2).

  STATUS: Live since 2026-08-25 (Jordan: ship now, iterate later). Not lawyer-reviewed. Every substantive term
  below was written to match what the code actually does today:
    - $129 one-time purchase, non-expiring entitlement (hasLifetimeAccess)
    - 7-day refund window (Pricing.tsx FAQ), refunds processed manually in Stripe
    - refund revokes access (aswb-backend stripeFunctions.ts)
  If any of those change, this page has to change with them.
*/
import React from 'react';
import { Helmet } from 'react-helmet';
import { appHref } from '@/utils/appHref';

const LAST_UPDATED = 'August 25, 2026';
const SUPPORT_EMAIL = 'support@socialworktestprepacademy.com';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="mb-6">
    <h2 className="text-xl font-semibold mb-3 text-neutral-900">{title}</h2>
    {children}
  </section>
);

const TermsOfService: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Terms of Service - Social Work Test Prep Academy</title>
        <meta
          name="description"
          content="The terms that govern your use of Social Work Test Prep Academy: accounts, one-time purchase and access, refunds, acceptable use, and limitations."
        />
        <meta property="og:title" content="Terms of Service - Social Work Test Prep Academy" />
        <meta
          property="og:description"
          content="The terms that govern your use of Social Work Test Prep Academy, including accounts, purchases, refunds, and acceptable use."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.socialworktestprepacademy.com/terms" />
        <meta property="og:site_name" content="Social Work Test Prep Academy" />
        <meta name="twitter:card" content="summary_large_image" />
        {/* Clean canonical, never window.location.href (folds UTM into canonical). */}
        <link rel="canonical" href="https://www.socialworktestprepacademy.com/terms" />
      </Helmet>
      <div className="new-home-container">
        <div className="new-home-content">
          <div className="new-home-inner">
            <div className="terms-of-service">
              <h1 className="text-2xl sm:text-3xl font-bold mb-4 text-center text-neutral-900">
                Terms of Service
              </h1>
              <p className="text-base sm:text-lg mb-6 text-center text-neutral-600">
                Last updated: {LAST_UPDATED}
              </p>

              <Section title="1. Who we are and what this covers">
                <p className="mb-4 text-neutral-700">
                  Social Work Test Prep Academy (&quot;SWTPA,&quot; &quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) provides
                  online study tools for people preparing for social work licensing exams. These Terms of Service
                  (&quot;Terms&quot;) are a legal agreement between you and SWTPA and they cover your use of our website,
                  our iOS and Android apps, and anything else we offer that links to these Terms (together, the
                  &quot;Service&quot;). By creating an account, starting a free trial, or buying access, you agree to these
                  Terms. If you do not agree, please do not use the Service.
                </p>
              </Section>

              <Section title="2. What the Service is">
                <p className="mb-4 text-neutral-700">
                  The Service includes practice questions with explanations, full-length practice exams, a diagnostic
                  assessment, flashcards, progress analytics, and AI-assisted clinical simulations. Content is
                  reviewed by a licensed clinical social worker. We add, change, and retire features over time, and we
                  may do so without notice, though we will not remove access you have paid for.
                </p>
                <p className="mb-4 text-neutral-700">
                  The Service is a study aid. It is not clinical advice, legal advice, or supervision, and it does not
                  create a therapist-client or supervisor-supervisee relationship with anyone at SWTPA.
                </p>
              </Section>

              <Section title="3. Your account">
                <p className="mb-4 text-neutral-700">
                  You need an account to use most of the Service. You must be at least 18 years old, give us accurate
                  information, and keep your login credentials to yourself. Your account is personal to you. You are
                  responsible for everything that happens under your account, so tell us at{' '}
                  <a className="text-primary-600 hover:underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>{' '}
                  if you think someone else is using it.
                </p>
                <p className="mb-4 text-neutral-700">
                  You can delete your account at any time from your account settings or by emailing us. Deleting your
                  account ends your access, including paid access, and it is not reversible.
                </p>
              </Section>

              <Section title="4. Free trial">
                <p className="mb-4 text-neutral-700">
                  We offer a free trial of the Service. We do not collect a payment card to start the trial, nothing
                  renews automatically, and there is nothing to cancel. When a trial ends, your access to paid
                  features stops until you purchase.
                </p>
              </Section>

              <Section title="5. Purchase, price, and what &quot;until you pass&quot; means">
                <p className="mb-4 text-neutral-700">
                  Full Access is a one-time payment of $129 (US dollars) plus any applicable tax. It is not a
                  subscription. Nothing auto-renews and there is no recurring charge.
                </p>
                <p className="mb-4 text-neutral-700">
                  &quot;Access until you pass&quot; means your Full Access entitlement does not expire on a timer. If your
                  exam date moves, or you have to sit the exam more than once, you keep the question bank, practice
                  exams, flashcards, analytics, and simulations at no additional charge. You will not be asked to pay
                  us a second time for the same exam preparation.
                </p>
                <p className="mb-4 text-neutral-700">
                  Two honest limits on that promise. First, we do not guarantee that you will pass; no honest prep
                  company can, and your result depends on your own preparation and performance. Second,
                  &quot;until you pass&quot; is not a promise to operate forever. If we ever discontinue the Service, we
                  will give paid users reasonable advance notice by email.
                </p>
                <p className="mb-4 text-neutral-700">
                  Purchases made through the Apple App Store or Google Play are also governed by that store&apos;s terms,
                  and refunds for those purchases are handled by the store.
                </p>
              </Section>

              <Section title="6. Refunds">
                <p className="mb-4 text-neutral-700">
                  If Full Access is not right for you, email{' '}
                  <a className="text-primary-600 hover:underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>{' '}
                  within 7 days of your purchase and we will refund you in full, no questions asked. That 7-day window
                  is the whole policy: there is no other deadline and no condition on how much of the Service you used.
                  We process approved refunds back to your original payment method within 2 to 3 business days.
                </p>
                <p className="mb-4 text-neutral-700">
                  A refund ends your paid access to the Service. After the 7-day window, purchases are final, and the
                  until-you-pass promise in Section 5 takes over: your access does not expire and you never pay twice.
                </p>
              </Section>

              <Section title="7. Our content, and what you may do with it">
                <p className="mb-4 text-neutral-700">
                  All practice questions, explanations, rationales, simulations, study plans, text, graphics, and
                  software in the Service are owned by SWTPA or its licensors and are protected by copyright and other
                  laws. Your purchase buys access, not ownership.
                </p>
                <p className="mb-4 text-neutral-700">
                  We grant you a personal, limited, non-exclusive, non-transferable, revocable license to use the
                  Service to study for your own licensing exam. You may not:
                </p>
                <ul className="list-disc pl-5 mb-4 text-neutral-700">
                  <li>copy, screenshot in bulk, scrape, export, or otherwise reproduce the question bank</li>
                  <li>republish, sell, sublicense, rent, or share our content with anyone else, paid or free</li>
                  <li>use our content to build, train, or improve a competing product or an AI model</li>
                  <li>share your account credentials, or let anyone else study on your account</li>
                  <li>use bots, crawlers, or automated tools against the Service</li>
                  <li>attempt to bypass the paywall, reverse engineer the Service, or interfere with its security</li>
                </ul>
                <p className="mb-4 text-neutral-700">
                  We may suspend or terminate an account that does any of the above, without a refund. Credential
                  sharing and question-bank redistribution are the two things we enforce most seriously, because the
                  bank is the product.
                </p>
              </Section>

              <Section title="8. Your content">
                <p className="mb-4 text-neutral-700">
                  You keep ownership of anything you submit, such as feedback, support messages, simulation responses,
                  and testimonials. You give us permission to store and use that material to run and improve the
                  Service. If you send us a testimonial and agree to it being used in marketing, we may publish it
                  along with the first name and credential you give us. You can withdraw that permission at any time
                  by emailing us, and we will stop using it going forward.
                </p>
              </Section>

              <Section title="9. Acceptable use">
                <p className="mb-4 text-neutral-700">
                  Use the Service lawfully and respectfully. Do not upload anything unlawful, harassing, or infringing,
                  do not impersonate anyone, do not submit real client information or other protected health
                  information into the Service (including into simulations or Ask Pam), and do not use the Service in a
                  way that burdens our infrastructure or harms other users.
                </p>
              </Section>

              <Section title="10. No affiliation with ASWB, and no guarantee">
                <p className="mb-4 text-neutral-700">
                  ASWB is a registered trademark of the Association of Social Work Boards, which is not affiliated with
                  and does not endorse SWTPA. We are an independent test prep company. We are not affiliated with,
                  endorsed by, or connected to any licensing board or any official exam administrator, and we do not
                  have access to actual exam questions.
                </p>
                <p className="mb-4 text-neutral-700">
                  We do not guarantee that you will pass any exam, that you will achieve any particular score, or that
                  you will meet any licensing requirement. Licensing rules and exam eligibility are set by your state
                  or jurisdiction and you are responsible for confirming them.
                </p>
              </Section>

              <Section title="11. Service availability">
                <p className="mb-4 text-neutral-700">
                  We work to keep the Service running, but we do not promise it will be uninterrupted or error free.
                  We may need to take it down for maintenance, and third-party providers we rely on can have outages.
                  The Service is provided &quot;as is&quot; and &quot;as available,&quot; without warranties of any kind,
                  whether express or implied, including implied warranties of merchantability, fitness for a particular
                  purpose, and non-infringement, to the fullest extent the law allows.
                </p>
              </Section>

              <Section title="12. Limitation of liability">
                <p className="mb-4 text-neutral-700">
                  To the fullest extent permitted by law, SWTPA and its owners, employees, and contractors are not
                  liable for indirect, incidental, special, consequential, or punitive damages, or for lost profits,
                  lost opportunities, lost licensure, or exam outcomes, arising out of or relating to your use of the
                  Service. Our total liability for any claim relating to the Service is limited to the amount you paid
                  us in the 12 months before the claim arose.
                </p>
                <p className="mb-4 text-neutral-700">
                  Some jurisdictions do not allow certain limitations, so parts of this section may not apply to you.
                </p>
              </Section>

              <Section title="13. Indemnity">
                <p className="mb-4 text-neutral-700">
                  You agree to cover SWTPA for claims, losses, and reasonable legal costs that come from your misuse of
                  the Service, your violation of these Terms, or your violation of someone else&apos;s rights.
                </p>
              </Section>

              <Section title="14. Suspension and termination">
                <p className="mb-4 text-neutral-700">
                  You may stop using the Service at any time. We may suspend or terminate your access if you breach
                  these Terms, if we are required to by law, or if we discontinue the Service. If we terminate paid
                  access for a reason other than your breach, we will refund a fair portion of what you paid. Sections
                  covering content ownership, disclaimers, liability, indemnity, and governing law survive termination.
                </p>
              </Section>

              <Section title="15. Privacy">
                <p className="mb-4 text-neutral-700">
                  Our{' '}
                  <a className="text-primary-600 hover:underline" href={appHref('/privacy-policy')}>Privacy Policy</a>{' '}
                  explains what we collect, how we use it, and the choices you have. It is part of these Terms.
                </p>
              </Section>

              <Section title="16. Changes to these Terms">
                <p className="mb-4 text-neutral-700">
                  We may update these Terms. When we do, we will change the &quot;Last updated&quot; date at the top of
                  this page, and for material changes we will give notice by email or in the app before they take
                  effect. Continuing to use the Service after a change means you accept the updated Terms. Changes are
                  never applied retroactively to a refund request you already made.
                </p>
              </Section>

              <Section title="17. Governing law">
                <p className="mb-4 text-neutral-700">
                  These Terms are governed by the laws of the State of Washington, without regard to its conflict of
                  law rules. Any dispute that is not resolved informally will be brought in the state or federal courts
                  located in Washington State, and you and SWTPA each consent to that jurisdiction. Nothing here stops
                  you from bringing a claim in small claims court.
                </p>
              </Section>

              <Section title="18. Everything else">
                <p className="mb-4 text-neutral-700">
                  These Terms, together with the Privacy Policy and our Disclosures page, are the entire agreement
                  between you and SWTPA about the Service. If a provision is found unenforceable, the rest stays in
                  effect. Our not enforcing a provision right away does not waive it. You may not transfer these Terms;
                  we may transfer them to a successor if our business is sold.
                </p>
              </Section>

              <Section title="19. Contact us">
                <p className="mb-4 text-neutral-700">
                  Questions about these Terms, refunds, or your account go to{' '}
                  <a className="text-primary-600 hover:underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
                  We read every message.
                </p>
              </Section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TermsOfService;
